import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-hot-toast';
import S from './NotificationSettingsStyles';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import useGetNotificationSettings from '../../API/hooks/notifications/useGetNotificationSettings';
import { notificationSettings as defaultNotificationSettings } from '../../constants/UserConfig';
import usePatchNotificationSettings from '../../API/hooks/notifications/usePatchNotificationSettings';
import CheckBox from '../common/CheckBox/CheckBox';
import { Button } from '../ui/Button/Button';
import { NotificationSettingsItems } from '../../API/types/Notifications';

type Props = {
  toggleModal: (closed: boolean) => void;
};

const notificationsList = [
  'devStreamEventFcw',
  'devStreamEventLdw',
  'devStreamEventPcw',
  'devStreamEventOverspeed',
  'devStreamEventWireCut',
  'devStreamEventDrowsiness',
  'devStreamEventEyesOffRoad',
  'devStreamEventCameraBlockageExteriorTotal',
  'devStreamEventCameraBlockageExteriorPartial',
  'devStreamEventCameraBlockageInteriorTotal',
  'devStreamEventCameraBlockageInteriorPartial',
  'devStreamEventMobilePhoneUsage',
  'devStreamEventTailgaiting',
  'devStreamEventAccidentCameraRemoval',
  'devStreamEventHardBrake',
  'devStreamEventHardAcceleration',
  'devStreamEventHardTurn',
  'devStreamEventHighG',
  'devStreamDriverNotFound',
  'drivingOvertime',
  'driverUnauthorized',
  'driverUnknown',
  'learnFaceFail',
  'learnFaceSuccess',
  'learnFaceTimedOut',
  'requestAvatarFail',
  'requestAvatarSuccess',
  'tripUpsertStart',
  'tripUpsertStop',
  'updateConfigFailed',
  'updateConfigSuccess',
  'videoUploadedFail',
  'videoUploadedSuccess',
  'alertVideoUploadedFail',
  'alertVideoUploadedSuccess',
  'intersectionsDirectionLeft',
  'intersectionsDirectionEntered',
];

function NotificationSettings({ toggleModal }: Props): JSX.Element {
  const onSubmitSuccess = (): any => {
    toast.success('Settings were saved');
    toggleModal(false);
  };
  const onSubmitFail = (errorMessage: string): any => toast.error(`Failed to save settings. Error: ${errorMessage}`);
  const fleetId = useGetFleetId();
  const { data: allSettings, isLoading, error } = useGetNotificationSettings(fleetId);
  const { mutate } = usePatchNotificationSettings(onSubmitSuccess, onSubmitFail);
  const [allWebSettingsChecked, setAllWebSettingsChecked] = useState(false);
  const [allEmailSettingsChecked, setAllEmailSettingsChecked] = useState(false);
  const noUserConfig = (error && (error as any).response.status === 404) as boolean;

  const formik = useFormik({
    initialValues: defaultNotificationSettings,
    onSubmit: (values) => {
      mutate({ fleetId, ...values });
    },
    validateOnMount: true,
  });

  const checkAllSelected = (type: 'notificationByWeb' | 'notificationByEmail'): boolean => notificationsList.every((n) => formik.values[n as keyof typeof formik.values]?.[type] !== false);

  useEffect(() => {
    formik.setSubmitting(false);
    setAllWebSettingsChecked(checkAllSelected('notificationByWeb'));
    setAllEmailSettingsChecked(checkAllSelected('notificationByEmail'));
  }, [formik.values]);

  useEffect(() => {
    const updatedConfig = JSON.parse(JSON.stringify(defaultNotificationSettings));
    Object.keys(updatedConfig).forEach((key: string) => {
      const defaultSetting = defaultNotificationSettings[key as keyof NotificationSettingsItems];
      const apiSetting = allSettings?.[key as keyof NotificationSettingsItems];

      updatedConfig[key] = {
        ...defaultSetting,
        notificationByWeb: apiSetting?.notificationByWeb ?? defaultSetting.notificationByWeb,
        notificationByEmail: apiSetting?.notificationByEmail ?? defaultSetting.notificationByEmail,
      };
    });
    formik.setValues(updatedConfig, false);
  }, [allSettings]);

  const handleCheckboxChange = (id: string, state: boolean): void => {
    formik.setFieldValue(id, state);
    formik.setSubmitting(false);
  };

  const handleAllCheckboxChange = (id: string, state: boolean): void => {
    if (id === 'notificationByWeb') {
      setAllWebSettingsChecked(state);
      notificationsList.forEach((n: string) => formik.setFieldValue(`${n}.notificationByWeb`, state));
    } else if (id === 'notificationByEmail') {
      setAllEmailSettingsChecked(state);
      notificationsList.forEach((n: string) => formik.setFieldValue(`${n}.notificationByEmail`, state));
    }
    formik.setSubmitting(false);
  };

  return (
    <S.Wrapper>
      <form onSubmit={formik.handleSubmit}>
        <div className="notification-settings">
          {isLoading && (<div className="loading">settings are loading...</div>)}
          {(!isLoading && noUserConfig) && (<div className="loading error">No setting found</div>)}
          {(!isLoading && !noUserConfig) && (
            <>
              <S.Row>
                <p> </p>
                <CheckBox
                  label="Select all"
                  name="notificationByWeb"
                  state={allWebSettingsChecked}
                  onChange={handleAllCheckboxChange}
                  disabled={isLoading}
                />
                <CheckBox
                  label="Select all"
                  name="notificationByEmail"
                  state={allEmailSettingsChecked}
                  onChange={handleAllCheckboxChange}
                  disabled={isLoading}
                />
              </S.Row>
              <h4>Alerts:</h4>
              <S.Row>
                <p>Forward collision warning:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventFcw.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventFcw.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventFcw.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventFcw.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Pedestrian collision warning:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventPcw.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventPcw.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventPcw.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventPcw.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Lane departure warning:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventLdw.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventLdw.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventLdw.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventLdw.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Over speeding warning:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventOverspeed.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventOverspeed.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventOverspeed.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventOverspeed.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Power supply cut:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventWireCut.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventWireCut.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventWireCut.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventWireCut.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Drowsiness warning:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventDrowsiness.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventDrowsiness.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventDrowsiness.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventDrowsiness.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Eyes off the road:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventEyesOffRoad.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventEyesOffRoad.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventEyesOffRoad.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventEyesOffRoad.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Int. camera is totally blocked:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventCameraBlockageInteriorTotal.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventCameraBlockageInteriorTotal.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventCameraBlockageInteriorTotal.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventCameraBlockageInteriorTotal.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Int. camera is partially blocked:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventCameraBlockageInteriorPartial.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventCameraBlockageInteriorPartial.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventCameraBlockageInteriorPartial.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventCameraBlockageInteriorPartial.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Ext. camera is totally blocked:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventCameraBlockageExteriorTotal.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventCameraBlockageExteriorTotal.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventCameraBlockageExteriorTotal.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventCameraBlockageExteriorTotal.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Ext. camera is partially blocked:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventCameraBlockageExteriorPartial.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventCameraBlockageExteriorPartial.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventCameraBlockageExteriorPartial.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventCameraBlockageExteriorPartial.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Mobile phone usage:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventMobilePhoneUsage.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventMobilePhoneUsage.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventMobilePhoneUsage.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventMobilePhoneUsage.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Tailgating warning:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventTailgaiting.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventTailgaiting.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventTailgaiting.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventTailgaiting.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Camera removal warning:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventAccidentCameraRemoval.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventAccidentCameraRemoval.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventAccidentCameraRemoval.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventAccidentCameraRemoval.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Hard brake warning:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventHardBrake.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventHardBrake.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventHardBrake.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventHardBrake.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Hard acceleration warning:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventHardAcceleration.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventHardAcceleration.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventHardAcceleration.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventHardAcceleration.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Hard turn warning:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventHardTurn.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventHardTurn.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventHardTurn.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventHardTurn.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>High G warning:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamEventHighG.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamEventHighG.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamEventHighG.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamEventHighG.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Driver Obstruction:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.devStreamDriverNotFound.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="devStreamDriverNotFound.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.devStreamDriverNotFound.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="devStreamDriverNotFound.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Driving Overtime warning:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.drivingOvertime.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="drivingOvertime.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.drivingOvertime.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="drivingOvertime.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Unauthorized driver detected:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.driverUnauthorized.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="driverUnauthorized.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.driverUnauthorized.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="driverUnauthorized.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Unknown driver detected:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.driverUnknown.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="driverUnknown.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.driverUnknown.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="driverUnknown.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <h4>Other:</h4>
              <S.Row>
                <p>Learn Face request was successful:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.learnFaceSuccess.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="learnFaceSuccess.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.learnFaceSuccess.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="learnFaceSuccess.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Learn Face request was failed:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.learnFaceFail.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="learnFaceFail.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.learnFaceFail.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="learnFaceFail.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Learn Face request was Timed out:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.learnFaceTimedOut.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="learnFaceTimedOut.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.learnFaceTimedOut.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="learnFaceTimedOut.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Avatar request was successful:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.requestAvatarSuccess.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="requestAvatarSuccess.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.requestAvatarSuccess.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="requestAvatarSuccess.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Avatar request was failed:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.requestAvatarFail.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="requestAvatarFail.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.requestAvatarFail.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="requestAvatarFail.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Trip started:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.tripUpsertStart.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="tripUpsertStart.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.tripUpsertStart.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="tripUpsertStart.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Trip was ended:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.tripUpsertStop.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="tripUpsertStop.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.tripUpsertStop.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="tripUpsertStop.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Device config was updated successfuly:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.updateConfigSuccess.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="updateConfigSuccess.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.updateConfigSuccess.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="updateConfigSuccess.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Device config update was failed:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.updateConfigFailed.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="updateConfigFailed.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.updateConfigFailed.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="updateConfigFailed.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Custom VoD upload was successful:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.videoUploadedSuccess.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="videoUploadedSuccess.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.videoUploadedSuccess.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="videoUploadedSuccess.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Custom VoD upload was failed:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.videoUploadedFail.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="videoUploadedFail.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.videoUploadedFail.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="videoUploadedFail.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Alert video upload was successful:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.alertVideoUploadedSuccess.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="alertVideoUploadedSuccess.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.alertVideoUploadedSuccess.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="alertVideoUploadedSuccess.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Alert video upload was failed:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.alertVideoUploadedFail.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="alertVideoUploadedFail.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.alertVideoUploadedFail.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="alertVideoUploadedFail.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Vehicle entered geofence area:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.intersectionsDirectionEntered.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="intersectionsDirectionEntered.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.intersectionsDirectionEntered.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="intersectionsDirectionEntered.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
              <S.Row>
                <p>Vehicle left geofence area:</p>
                <CheckBox
                  label="Notifications by web"
                  state={formik.values.intersectionsDirectionLeft.notificationByWeb}
                  onChange={handleCheckboxChange}
                  name="intersectionsDirectionLeft.notificationByWeb"
                  disabled={isLoading}
                />
                <CheckBox
                  label="Notifications by e-mail"
                  state={formik.values.intersectionsDirectionLeft.notificationByEmail}
                  onChange={handleCheckboxChange}
                  name="intersectionsDirectionLeft.notificationByEmail"
                  disabled={isLoading}
                />
              </S.Row>
            </>
          )}
        </div>
        <S.Buttons>
          <Button
            className="save-button"
            height="50px"
            type="submit"
            disabled={formik.isSubmitting || isLoading || noUserConfig}
          >
            Save
          </Button>
          <Button
            height="50px"
            onClick={() => toggleModal(false)}
            type="button"
          >
            Close
          </Button>
        </S.Buttons>

      </form>
    </S.Wrapper>
  );
}

export default NotificationSettings;
